import { SelfContainedCommand, CommandExecutionContext } from "types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "types/standardToolResults";
import { createFileLogger } from '../../../utils/logger';
import { entityCreationRegistry } from '../../../services/entityCreationRegistry';

const log = createFileLogger('PRESENTATIONS_COMMANDS');

export const presentationsCreateCommand: SelfContainedCommand = {
    id: 'presentations_create',
    description: '🎭 CREATE: Create a new presentation entity and optionally open it in a window. By default creates a window unless createWindow=false is passed.',
    autonomyLevel: 'auto',
    visualTreatment: 'demonstrative',
    
    schema: {
        parameters: [
            {
                name: 'name',
                type: 'string',
                description: 'Name for the new presentation (default: "Untitled Slides")',
                required: false,
                defaultValue: 'Untitled Slides',
                validator: (value: string) => {
                    if (value && value.trim().length === 0) return 'Presentation name cannot be empty';
                    if (value && value.length > 100) return 'Presentation name too long (max 100 chars)';
                    return null;
                },
                examples: ['Q4 Results', 'Product Launch', 'Team Meeting']
            },
            {
                name: 'spaceId',
                type: 'string',
                description: 'The ID of the space to create the presentation in. Will use context spaceId if not provided.',
                required: false,
                examples: ['space-123', 'space-abc-def']
            },
            {
                name: 'folderId',
                type: 'string',
                description: 'The ID of the folder to create the presentation in. Leave empty for root level.',
                required: false,
                examples: ['folder-123', null]
            },
            {
                name: 'createWindow',
                type: 'boolean',
                description: 'Whether to create and open a window for the presentation. Default is true.',
                required: false,
                defaultValue: true,
                examples: [true, false]
            }
        ]
    },
    
    examples: {
        good: [
            {
                input: { name: 'Q4 Results' },
                expectedOutput: createSuccessResult('Presentation created successfully', {
                    entityId: 'presentation-123',
                    name: 'Q4 Results',
                    entityType: 'Presentation'
                }),
                description: 'Create a new presentation with a custom name',
                context: 'When starting a new presentation for a specific purpose'
            }
        ],
        bad: [
            {
                input: { name: '' },
                error: 'Presentation name cannot be empty',
                why: 'Empty names are not allowed',
                fix: 'Provide a non-empty name or omit the parameter to use default'
            }
        ]
    },
    
    performance: {
        expectedDuration: 500,
        complexity: 'low'
    },
    
    handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
        const startTime = Date.now();
        const { name = 'Untitled Slides', spaceId: providedSpaceId, folderId, createWindow = true } = params;
        const spaceId = providedSpaceId || context.spaceId;
        
        try {
            log.log('[presentations_create] Creating new presentation:', { name, spaceId });
            
            console.log('[presentations_create] 🔍 Received folderId parameter:', {
                folderId: folderId,
                folderIdType: typeof folderId,
                isNull: folderId === null,
                isUndefined: folderId === undefined,
                allParams: params
            });
            
            // ✅ ARCHITECTURE: Use entityStore actions for proper store/CRDT sync
            const now = new Date().toISOString();
            const newPresentationEntityData = {
                entityType: 'Presentation',
                name: name.trim(),
                spaceId: spaceId,
                skeleton: {
                    '@type': 'Presentation',
                    version: 'v2',
                    architecture: 'unified',
                    groupIds: folderId ? [folderId] : []
                },
                created_at: now,
            };
            
            log.log('[presentations_create] Creating presentation via entityStore:', { 
                spaceId: newPresentationEntityData.spaceId,
                name: newPresentationEntityData.name,
                folderId
            });
            
            const useEntityStore = (await import('../../../stores/entityStore')).default;
            const newPresentation = await useEntityStore.getState().actions.createEntity(newPresentationEntityData);
            const entityId = newPresentation._id || newPresentation.id;
            const presentationLlmId = (newPresentation as any).llmId;
            
            log.log('[presentations_create] ✅ Presentation created:', { 
                entityId, 
                presentationLlmId,
                name: newPresentation.name 
            });
            
            // ✅ IMPORTANT: Register with dataspace tree if we have folderId
            if (folderId && entityId) {
                try {
                    const { default: useDataspaceTreeStore } = await import('../../../stores/dataspaceTreeStore');
                    const treeStore = useDataspaceTreeStore.getState();
                    const edgeKey = `${folderId}:${entityId}`;
                    
                    treeStore._applyAddNode(edgeKey, {
                        id: entityId,
                        parentId: folderId,
                        pos: "M",
                        expanded: false,
                        kind: 'canonical'
                    });
                    
                    log.log('[presentations_create] ✅ Added to dataspace tree:', { folderId, entityId });
                } catch (treeError) {
                    log.warn('[presentations_create] ⚠️ Failed to add to tree (non-fatal):', treeError);
                }
            }
            
            let windowInfo = null;
            
            // Create window only if requested (default: true for backwards compatibility)
            if (createWindow !== false && spaceId) {
                try {
                    const { useCommandRegistryStore } = await import('../../../stores/commandRegistryStore');
                    const { executeCommand } = useCommandRegistryStore.getState();
                    
                    console.log('[presentations_create] Opening window for entity:', {
                        entityId,
                        presentationLlmId,
                        entityType: newPresentation.entityType,
                        name: newPresentation.name
                    });
                    
                    const result = (await executeCommand('space_open_window', {
                        entityId
                    })) as import('../../../types/standardToolResults').StandardToolResult;
                    
                    if (result.status !== 'success') {
                        log.error('[presentations_create] Failed to open window:', result.message);
                        throw new Error(result.message || 'Failed to open window');
                    }
                    
                    const createdWindowId = result.data?.windowId;
                    
                    windowInfo = {
                        id: createdWindowId,
                        title: name.trim(),
                        appType: 'presentations-v2',
                        entityId,
                        entityLlmId: presentationLlmId
                    };
                    
                    log.log('[presentations_create] ✅ Window created:', { windowId: createdWindowId });
                } catch (windowError) {
                    log.error('[presentations_create] ❌ Failed to create window:', windowError);
                }
            }
            
            const duration = Date.now() - startTime;
            
            // ✅ STANDARDIZED: Return entityId at top level for sidebar extraction
            return createSuccessResult(
                `Created presentation: ${name}`,
                {
                    entityId: entityId,
                    name: newPresentation.name,
                    entityType: 'Presentation',
                    presentation: {
                        id: entityId,
                        llmId: presentationLlmId,
                        name: newPresentation.name,
                        entityType: newPresentation.entityType || 'Presentation',
                        created_at: newPresentation.created_at,
                        icon: newPresentation.icon || '🎭'
                    },
                    window: windowInfo
                },
                {
                    format: 'json',
                    metadata: {
                        executionTime: duration,
                        method: 'self-contained-v2',
                        affectedEntities: [entityId]
                    }
                }
            );
            
        } catch (error) {
            const duration = Date.now() - startTime;
            log.error('[presentations_create] Failed to create presentation:', error);
            
            return createErrorResult(
                'Failed to create presentation',
                error instanceof Error ? error.message : 'Unknown error occurred during presentation creation',
                'execution',
                {
                    metadata: {
                        executionTime: duration
                    }
                }
            );
        }
    }
};

// Register in entity creation registry
entityCreationRegistry.register({
    commandId: 'presentations_create',
    entityType: 'Presentation',
    displayName: 'Presentation',
    icon: { type: 'emoji', emoji: '🎭' },
    description: 'Create a slide presentation',
    canCreateInFolder: true,
    openAfterCreate: true,
    category: 'document'
});

